import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, PHONE_DISPLAY, PHONE_TEL } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, LogIn, LayoutDashboard, Shield, Phone, Mail, Store, UserPlus, CalendarCheck, UserRound } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const dashHref = user?.role === "seller" ? "/vendeur" : user?.role === "admin" ? "/admin" : "/dashboard";
  const dashLabel = user?.role === "seller" ? "Espace cédant" : user?.role === "admin" ? "Tableau de bord" : "Espace acheteur";

  const links = [
    { to: "/", label: "Accueil" },
    { to: "/catalogue", label: "Entreprises à céder" },
    { to: "/reprendre-une-entreprise", label: "Reprendre" },
    { to: "/entreprises-vendues", label: "Nos dernières cessions" },
    { to: "/conseils", label: "Conseils" },
    { to: "/contact", label: "Contact" },
  ];
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header data-testid="main-header" className="sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-[#1B2A4A] text-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-10 text-[13px]">
          <div className="flex items-center gap-5">
            <a href={`tel:${PHONE_TEL}`} data-testid="header-phone" className={`${user ? "hidden sm:flex" : "flex"} items-center gap-2 hover:text-[#E0C173] transition-colors`}>
              <Phone className="w-3.5 h-3.5 text-[#E0C173]" strokeWidth={2} /> {PHONE_DISPLAY}
            </a>
            <a href="mailto:contact@proac-transmission.fr" className="hidden sm:flex items-center gap-2 hover:text-[#E0C173] transition-colors">
              <Mail className="w-3.5 h-3.5 text-[#E0C173]" strokeWidth={2} /> contact@proac-transmission.fr
            </a>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 whitespace-nowrap">
            {user ? (
              <>
                {user.role === "admin" && (
                  <button data-testid="topbar-onsite" onClick={() => navigate("/admin/rdv")} className="flex items-center gap-1.5 whitespace-nowrap text-[#E0C173] font-semibold hover:text-white transition-colors">
                    <CalendarCheck className="w-3.5 h-3.5" strokeWidth={2} /> Mode RDV
                  </button>
                )}
                {user.role === "admin" && (
                  <button data-testid="topbar-admin" onClick={() => navigate("/admin")} className="hidden sm:flex items-center gap-1.5 hover:text-[#E0C173] transition-colors">
                    <Shield className="w-3.5 h-3.5" /> Admin
                  </button>
                )}
                <button data-testid="topbar-account" onClick={() => navigate("/mon-compte")} className="flex items-center gap-1.5 whitespace-nowrap hover:text-[#E0C173] transition-colors"><UserRound className="w-3.5 h-3.5" strokeWidth={2} /> <span className="hidden sm:inline">Mon compte</span></button>
                <button data-testid="topbar-dashboard" onClick={() => navigate(dashHref)} className="flex items-center gap-1.5 hover:text-[#E0C173] transition-colors">
                  <LayoutDashboard className="w-3.5 h-3.5" /> <span className="hidden sm:inline max-w-[120px] truncate">{dashLabel}</span>
                </button>
                <button data-testid="topbar-logout" onClick={handleLogout} aria-label="Déconnexion" className="flex items-center gap-1.5 hover:text-[#E0C173] transition-colors">
                  <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </>
            ) : (
              <button data-testid="header-login-btn" onClick={() => navigate("/connexion")} className="flex items-center gap-1.5 hover:text-[#E0C173] transition-colors">
                <LogIn className="w-3.5 h-3.5 text-[#E0C173]" /> Connexion
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="bg-white border-b border-gray-200 shadow-[0_4px_24px_rgba(27,42,74,0.06)]">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 px-5 sm:px-8 py-2">
          <Link to="/" data-testid="logo-link" className="flex items-center h-full group shrink-0">
            <img src={LOGO_URL} alt="PROAC Transmission" className="h-16 sm:h-[76px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.04]" />
          </Link>

          <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label}`} className="relative py-1.5 whitespace-nowrap text-[15px] font-medium text-[#475569] hover:text-[#1B2A4A] transition-colors group">
                {l.label}
                <span className={`absolute left-0 -bottom-0.5 h-[2px] bg-[#E0C173] transition-all duration-300 ${isActive(l.to) ? "w-full" : "w-0 group-hover:w-full"}`} />
              </Link>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3">
            {user ? (
              <Button data-testid="header-dashboard-btn" className="rounded-full bg-[#1B2A4A] hover:bg-[#0D1629]" onClick={() => navigate(dashHref)}>
                <LayoutDashboard className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {dashLabel}
              </Button>
            ) : (
              <>
                <Button data-testid="header-buyer-btn" variant="outline" className="rounded-full border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white transition-colors" onClick={() => navigate("/inscription?role=buyer")}>
                  <UserPlus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Devenir acheteur
                </Button>
                <button data-testid="header-seller-btn" className="flex items-center gap-2.5 rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] pl-4 pr-5 py-1.5 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all" onClick={() => navigate("/rdv-decouverte")}>
                  <Store className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                  <span className="flex flex-col items-start leading-tight">
                    <span className="text-sm font-semibold whitespace-nowrap">Vendre mon entreprise</span>
                    <span className="text-[10.5px] font-medium whitespace-nowrap text-[#1B2A4A]/75">Fixer un RDV découverte <strong className="text-[#1B2A4A]">GRATUIT</strong></span>
                  </span>
                </button>
              </>
            )}
          </div>

          <button data-testid="mobile-menu-toggle" className="xl:hidden w-11 h-11 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center" onClick={() => setOpen(!open)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-b border-gray-200 bg-white px-5 py-5 space-y-2">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className={`block px-4 py-2.5 rounded-full font-medium ${isActive(l.to) ? "bg-[#1B2A4A] text-white" : "text-[#1B2A4A] hover:bg-[#1B2A4A]/5"}`}>
              {l.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
            {user ? (
              <>
                <Button data-testid="mobile-account-btn" variant="outline" className="rounded-full w-full" onClick={() => { setOpen(false); navigate("/mon-compte"); }}><UserRound className="w-4 h-4 mr-1.5" /> Mon compte</Button>
                {user.role === "admin" && <Button data-testid="mobile-onsite-btn" className="rounded-full w-full bg-[#E0C173] text-[#1B2A4A] font-semibold" onClick={() => { setOpen(false); navigate("/admin/rdv"); }}><CalendarCheck className="w-4 h-4 mr-1.5" /> Mode RDV : saisie sur place</Button>}
                {user.role === "admin" && <Button className="rounded-full w-full bg-[#1B2A4A]" onClick={() => { setOpen(false); navigate("/admin"); }}>Admin</Button>}
                <Button variant="outline" className="rounded-full w-full border-[#1B2A4A] text-[#1B2A4A]" onClick={() => { setOpen(false); navigate(dashHref); }}>{dashLabel}</Button>
                <Button className="rounded-full w-full bg-[#1B2A4A]" onClick={handleLogout}>Déconnexion</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" className="rounded-full w-full justify-start text-[#1B2A4A]" onClick={() => { setOpen(false); navigate("/connexion"); }}><LogIn className="w-4 h-4 mr-1.5" /> Connexion</Button>
                <Button variant="outline" className="rounded-full w-full border-[#1B2A4A] text-[#1B2A4A]" onClick={() => { setOpen(false); navigate("/inscription?role=buyer"); }}><UserPlus className="w-4 h-4 mr-1.5" /> Devenir acheteur</Button>
                <button data-testid="mobile-rdv-btn" className="rounded-full w-full bg-[#E0C173] text-[#1B2A4A] py-2.5 flex flex-col items-center leading-tight" onClick={() => { setOpen(false); navigate("/rdv-decouverte"); }}>
                  <span className="font-semibold flex items-center gap-1.5"><Store className="w-4 h-4" /> Vendre mon entreprise</span>
                  <span className="text-xs">Fixer un RDV découverte <strong>GRATUIT</strong></span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
